import { createClient } from "@sanity/client";
import imageUrlBuilder from "@sanity/image-url";
import { useMemo } from "react";
import { createQueryStore } from "@sanity/react-loader";

// True only when the site is loaded inside the Sanity Presentation tool iframe.
export const inPreview =
  typeof window !== "undefined" && window.self !== window.top;

export const client = createClient({
  projectId: process.env.REACT_APP_SANITY_PROJECT_ID as string,
  dataset: (process.env.REACT_APP_SANITY_DATASET as string) || "production",
  apiVersion: "2026-06-01",
  useCdn: !inPreview,
  // stega (Content Source Maps) is enabled ONLY inside the Presentation iframe,
  // so the public site's text, slugs and URLs stay clean.
  stega: {
    enabled: inPreview,
    studioUrl: "https://brownesports.sanity.studio",
  },
});

// Client-side loader store. ssr:false means useQuery fetches in the BROWSER with
// `client` (we pass it here — NOT via setServerClient, which is hard-guarded to
// server-only and throws in the browser). Inside Presentation, useLiveMode
// streams live DRAFT results over the comlink from the already-authenticated
// Studio, so no read token is ever needed in the browser.
const { useQuery, useLiveMode } = createQueryStore({ client, ssr: false });
export { useLiveMode };

const builder = imageUrlBuilder(client);

/** Build a CDN URL (with optional transforms) from a Sanity image source. */
export function urlFor(source: any) {
  return builder.image(source);
}

export type GroqResult<T> = {
  data: T | undefined;
  loading: boolean;
  error: any;
};

/**
 * Mirrors Apollo's `{ data, loading, error }`. Backed by the loader's useQuery:
 * a plain CDN fetch on the public site, and live/reactive inside the
 * Presentation tool — drafts stream in and update as an editor types.
 */
export function useGroq<T = any>(
  query: string,
  params: Record<string, any> = {}
): GroqResult<T> {
  // Stabilise params so useQuery doesn't re-subscribe on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableParams = useMemo(() => params, [JSON.stringify(params)]);
  const { data, loading, error } = useQuery<T>(query, stableParams);
  return {
    data: (data ?? undefined) as T | undefined,
    loading: loading ?? data === undefined,
    error,
  };
}
